import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import iconTg1 from '../icons/tg-1.svg';
import iconX1 from '../icons/x-1.svg';
import iconWeb from '../icons/website.svg';
import { projectName } from '../utils/constants.ts';

// Format number with 3 decimal places
const formatNumber = (num) => {
  if (!num || isNaN(num)) return '0.000';
  if (num >= 1e12) return (num / 1e12).toFixed(3) + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(3) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(3) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(3) + 'K';
  return num.toFixed(3);
};

// Format price with 6 decimal places
const formatPrice = (num) => {
  if (!num || isNaN(num)) return '0.000000';
  return num.toFixed(6);
};

// Calculate live time from startTime
const calculateLiveTime = (startTime) => {
  if (!startTime) return 'N/A';
  const now = Math.floor(Date.now() / 1000);
  const diff = now - startTime;
  
  if (diff < 0) return 'Not started';
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const LaunchpadCard = ({
  chainId,
  progress,
  tokenName,
  tokenSymbol,
  tokenDescription,
  logoUrl,
  bannerUrl,
  funAddress,
  depositedAmount,
  devAddress,
  marketCap,
  price,
  volume,
  startTime,
  website,
  twitter,
  telegram,
  BlockchainLogo,
  onClaim,
  isClaiming,
  hideTopBanner = false,
  accumulatedCreatorReward,
  nativeSymbol,
}) => {
  const link = `/buy/?chain=${chainId}&address=${funAddress}`;
  const { theme, toggleTheme } = useTheme();

  // Helper function to get fallback URL (try FlazePad format if token name format fails)
  const getFallbackUrl = (url, tokenName) => {
    if (!url || !tokenName) return url;
    try {
      // Extract the filename part after the last '/'
      const parts = url.split('/');
      const filename = parts[parts.length - 1];
      // Replace tokenName with projectName in filename
      // Format: tokenName-chainId-address.png -> FlazePad-chainId-address.png
      const escapedTokenName = tokenName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const fallbackFilename = filename.replace(new RegExp(`^${escapedTokenName}-`), `${projectName}-`);
      if (fallbackFilename !== filename) {
        parts[parts.length - 1] = fallbackFilename;
        return parts.join('/');
      }
    } catch (error) {
      console.error('Error constructing fallback URL:', error);
    }
    return url;
  };

  // State to track which URL format to use for logo
  // Start with tokenName format, will fallback to FlazePad format if needed
  const [logoUrlToUse, setLogoUrlToUse] = useState(logoUrl);
  const [logoHasTriedFallback, setLogoHasTriedFallback] = useState(false);

  // Reset state when props change
  useEffect(() => {
    setLogoUrlToUse(logoUrl);
    setLogoHasTriedFallback(false);
  }, [logoUrl]);

  // Create error handler that tries fallback format
  const handleLogoError = () => {
    if (!logoHasTriedFallback && logoUrl) {
      setLogoHasTriedFallback(true);
      const fallback = getFallbackUrl(logoUrl, tokenName);
      if (fallback !== logoUrl) {
        setLogoUrlToUse(fallback);
      }
    }
  };

  const handleClaimClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClaim && !isClaiming) {
      onClaim(funAddress, chainId);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden shadow-lg transition-all" style={{
      background: 'var(--card-bg)',
      border: '1px solid transparent',
      backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
    }}>
      <a href={link}>
        {/* Logo (replacing banner) - hidden when hideTopBanner */}
        {!hideTopBanner && (
          <div className="relative h-40 overflow-hidden flex items-center justify-center" style={{ background: 'var(--section-bg)' }}>
            {logoUrlToUse ? (
              <img
                src={logoUrlToUse}
                alt={tokenName}
                className="w-full h-full object-contain"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                onError={handleLogoError}
              />
            ) : (
              <div className="w-full h-full" style={{ background: 'var(--section-bg)' }} />
            )}
          </div>
        )}

        <div className={hideTopBanner ? 'p-4 pt-4' : 'p-4'}>
          {/* First Line: Logo, Token Name/Ticker, and Live Time */}
          <div className="flex items-center gap-3 mb-2">
            {/* Logo on the left */}
            <div className="flex-shrink-0">
              {logoUrlToUse ? (
                <img
                  src={logoUrlToUse}
                  alt={tokenName}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={handleLogoError}
                />
              ) : (
                <div className="w-10 h-10 rounded-full" style={{ background: 'var(--section-bg)' }} />
              )}
            </div>
            
            {/* Token Name and Ticker in the middle */}
            <div className="min-w-0 flex-1">
              <h3 className="font-bold truncate mb-1" style={{ color: 'var(--text-primary)' }}>
                {tokenName}
              </h3>
              <p className="text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>
                {tokenSymbol || 'N/A'}
              </p>
            </div>
            
            {/* Live Time on the right - always show */}
            <div className="flex-shrink-0 flex items-center">
              <span className="text-xs mr-2" style={{ color: 'var(--text-secondary)' }}>Live:</span>
              <span className="font-bold text-sm" style={{ color: 'var(--text-accent)' }}>
                {startTime != null && startTime !== undefined ? calculateLiveTime(Number(startTime)) : 'N/A'}
              </span>
            </div>
          </div>

          {/* Second Line: Created By (left) + Creator reward (right, same line) */}
          <div className="mb-2 flex items-center justify-between gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
            <span>Created by: {devAddress ? `${devAddress.slice(0, 4)}...${devAddress.slice(-3)}` : 'N/A'}</span>
            {(accumulatedCreatorReward != null && accumulatedCreatorReward > 0) && (
              <span className="flex-shrink-0">Creator reward: <span className="font-medium" style={{ color: 'var(--text-accent)' }}>{formatPrice(accumulatedCreatorReward)} {nativeSymbol || 'ETH'}</span></span>
            )}
          </div>

          {/* Fourth Line: MC, Volume, Price */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex flex-col">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>MC</span>
              <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                ${formatNumber(marketCap || 0)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Volume</span>
              <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                ${formatNumber(volume || depositedAmount || 0)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Price</span>
              <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                ${formatPrice(price || 0)}
              </span>
            </div>
          </div>

          {/* Last Line: Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1">
              <span style={{ color: 'var(--text-secondary)' }}>Progress</span>
              <span style={{ color: 'var(--text-accent)' }}>{Math.min(100, progress.toFixed(2))}%</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--progress-container-bg)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress >= 100 ? 100 : progress}%`,
                  background: 'var(--progress-fill)'
                }}
              />
            </div>
          </div>

          {/* Social Icons */}
          {(website || twitter || telegram) && (
            // <div className="flex justify-center gap-4 mt-3">
            //   {twitter && (
            //     <a
            //       href={twitter}
            //       target="_blank"
            //       rel="noopener noreferrer"
            //       onClick={e => e.stopPropagation()}
            //     >
            //       <img
            //         src={TwitterIcon}
            //         alt="Twitter"
            //         className="w-5 h-5 text-white"
            //       style={{ filter: 'var(--icon-filter)'}}
            //       />
            //     </a>
            //   )}
            //   {telegram && (
            //     <a
            //       href={telegram}
            //       target="_blank"
            //       rel="noopener noreferrer"
            //       onClick={e => e.stopPropagation()}
            //     >
            //       <img
            //         src={TelegramIcon}
            //         alt="Telegram"
            //         className="w-5 h-5"
            //       // style={{ filter: 'var(--icon-filter)' }}
            //       />
            //     </a>
            //   )}
            //   {website && (
            //     <a
            //       href={website}
            //       target="_blank"
            //       rel="noopener noreferrer"
            //       onClick={e => e.stopPropagation()}
            //     >
            //       <img
            //         src={WebsiteIcon}
            //         alt="Website"
            //         className="w-5 h-5"
            //       // style={{ filter: 'var(--icon-filter)' }}
            //       />
            //     </a>
            //   )}
            // </div>
            <div className='flex justify-center gap-4 mt-3'>
              {/* {twitter && (
                <a href="https://x.com/LoadingCoin" target="_blank" className="p-2" rel="noreferrer">
                  <img src={iconX1} alt='' className={`w-4 h-4 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a>
              )} */}
              {telegram && (
                <a href="https://t.me/FlazePad" target="_blank" className="p-2" rel="noreferrer">
                  <img src={iconTg1} alt='' className={`w-4 h-4 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a>
              )}
              {website && (
                <a href="https://flazepad.xyz" target="_blank" className="px-2 pt-1" rel="noreferrer">
                  <img src={iconWeb} alt='' className={`w-6 h-6 ${theme === 'light' ? 'filter invert' : ''}`} />
                </a>
              )}
            </div>
          )}
        </div>
      </a>

      {/* Claim Button - Outside link to prevent navigation */}
      {onClaim && (
        <div className="px-4 pb-4">
          <button
            onClick={handleClaimClick}
            disabled={isClaiming}
            className="w-full py-2 px-4 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: isClaiming ? 'var(--button-disabled-bg)' : 'var(--primary-gradient)',
              color: 'var(--button-text)',
              border: '1px solid var(--button-border)'
            }}
          >
            {isClaiming ? 'Claiming...' : 'Claim Rewards'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LaunchpadCard;
